import { type Fields, fieldStr } from './shared'

/**
 * Form-side model of the closed predicate grammar (crates/tools
 * `handlers/predicate.rs`): a term is `when` + exactly one comparison, or
 * `exists`. Pure helpers shared by the event_route form.
 */

export type Comparison = 'equals' | 'not_equals' | 'in' | 'in_ci' | 'contains'

/** The comparisons, in menu order; their words are in `resourcesCopy.forms.predicate`. */
export const COMPARISONS: readonly Comparison[] = [
  'equals',
  'not_equals',
  'in',
  'in_ci',
  'contains',
]

/** One editable term row; `exists` tests that `when` is non-empty. */
export interface TermRow {
  when: string
  op: Comparison | 'exists'
  value: string
}

const COMPARISON_KEYS = COMPARISONS

/** Comparisons whose value is a list. */
export const isList = (op: Comparison | 'exists'): boolean =>
  op === 'in' || op === 'in_ci'

export function splitList(s: string): string[] {
  return s
    .split(/[,\n]/)
    .map((x) => x.trim())
    .filter(Boolean)
}

/** The single comparison on a stored term (null when it has none), with
 * an `in` list joined by `listSep`. */
function comparisonOf(
  term: Record<string, unknown>,
  listSep = ', ',
): { op: Comparison; value: string } | null {
  const op = COMPARISON_KEYS.find((k) => term[k] != null)
  if (!op) return null
  const raw = term[op]
  const value = Array.isArray(raw) ? raw.map(String).join(listSep) : String(raw)
  return { op, value }
}

/** `when` + comparison as stored keys; null when the value is blank (the
 * server rejects empty comparisons rather than matching nothing). */
function comparisonBody(
  when: string,
  op: Comparison,
  value: string,
): Record<string, unknown> | null {
  if (isList(op)) {
    const list = splitList(value)
    return list.length > 0 ? { when, [op]: list } : null
  }
  const v = op === 'contains' ? value : value.trim()
  return v.trim() ? { when, [op]: v } : null
}

export function termBody(row: TermRow): Record<string, unknown> | null {
  const when = row.when.trim()
  if (!when) return null
  if (row.op === 'exists') return { exists: when }
  return comparisonBody(when, row.op, row.value)
}

/** A stored term as a row, or null when it doesn't fit one (both a
 * comparison and `exists`, or unknown keys). */
export function termRow(term: unknown): TermRow | null {
  if (!term || typeof term !== 'object') return null
  const t = term as Record<string, unknown>
  const keys = Object.keys(t).filter((k) => t[k] != null)
  if (keys.length === 1 && keys[0] === 'exists' && typeof t.exists === 'string')
    return { when: t.exists, op: 'exists', value: '' }
  const cmp = comparisonOf(t)
  if (!cmp || keys.length !== 2 || typeof t.when !== 'string') return null
  return { when: t.when, ...cmp }
}

/** All rows for `terms`, or null if any term doesn't fit a row. */
export function termRows(terms: unknown): TermRow[] | null {
  if (!Array.isArray(terms)) return null
  const rows = terms.map(termRow)
  return rows.every((r): r is TermRow => r !== null) ? rows : null
}

/** Serialized `all` rows (form state) back to rows. */
export function parseRows(raw: string | undefined): TermRow[] {
  return raw ? (JSON.parse(raw) as TermRow[]) : []
}

/** The predicate fields as stored keys, plus whatever the form carried
 * through untouched. A blank value means no top-level comparison (match
 * every event): the server rejects an empty comparison rather than
 * matching nothing. */
export function predicateBody(f: Fields): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  const top = comparisonBody(
    f.when || '{event_type}',
    (f.op || 'equals') as Comparison,
    f.value ?? '',
  )
  if (top) Object.assign(body, top)
  const all = parseRows(f.all)
    .map(termBody)
    .filter((t) => t !== null)
  if (all.length > 0) body.all = all
  if (f.preserved) Object.assign(body, JSON.parse(f.preserved))
  return body
}

/** Stored data → predicate form fields. Keys in `preservedKeys`, and `all`
 * terms the rows can't represent, ride through edits verbatim. */
export function predicateFields(
  d: unknown,
  preservedKeys: readonly string[],
): Fields {
  const r = (d ?? {}) as Record<string, unknown>
  const cmp = comparisonOf(r, '\n')
  const allRows = termRows(r.all)
  const kept: Record<string, unknown> = Object.fromEntries(
    preservedKeys.filter((k) => r[k] != null).map((k) => [k, r[k]]),
  )
  if (Array.isArray(r.all) && r.all.length > 0 && !allRows) kept.all = r.all
  return {
    preserved: Object.keys(kept).length > 0 ? JSON.stringify(kept) : '',
    when: fieldStr(d, 'when', '{event_type}'),
    op: cmp?.op ?? 'equals',
    value: cmp?.value ?? '',
    all: allRows && allRows.length > 0 ? JSON.stringify(allRows) : '',
  }
}
